import { convertDocumentInfoToChatSource } from "@mirage/service-conversation/utils/convertDocumentInfoToChatSource";

import type {
  ChatConversationMessage,
  ChatResponse,
} from "@mirage/mosaics/Chat/types";
import type { ConversationMessage } from "@mirage/service-conversation/types";
import { CALENDAR_AGENT_ID } from "@mirage/service-conversation/types";

export const convertToChatMessage = (
  message: ConversationMessage,
): ChatConversationMessage => {
  switch (message.messageType.case) {
    case "userMessage":
      return {
        type: "message",
        text: message.messageType.value.text ?? "",
        role: "user",
        referencingSources: convertDocumentInfoToChatSource(
          message.messageType.value.attachments,
        ),
      };
    case "assistantMessage": {
      const agentId = message.messageType.value.agentId;
      let parsedJson: ChatResponse | undefined;

      // Parse JSON content based on agent ID
      if (
        agentId === CALENDAR_AGENT_ID &&
        message.jsonContent?.calendarEvents
      ) {
        parsedJson = {
          type: "calendar",
          calendarEvents: message.jsonContent.calendarEvents,
        };
      }
      const result: ChatConversationMessage = {
        type: "message",
        text: message.messageType.value.text ?? "",
        json: parsedJson,
        agentId,
        role: "assistant",
        toolCalls: message.messageType.value.toolCalls,
      };

      // Add referencingSources if there are cited documents
      if (
        message.messageType.value.cited &&
        message.messageType.value.cited.length > 0
      ) {
        result.referencingSources = convertDocumentInfoToChatSource(
          message.messageType.value.cited,
        );
      }

      // Add additionalContext if there are context documents
      if (
        message.messageType.value.context &&
        message.messageType.value.context.length > 0
      ) {
        result.additionalContext = convertDocumentInfoToChatSource(
          message.messageType.value.context,
        );
      }

      return result;
    }
    case "systemMessage":
      return {
        type: "message",
        text: message.messageType.value.text ?? "",
        role: "system",
        referencingSources: [],
      };
    case "toolMessage":
      return {
        type: "tool_call",
        role: "tool",
        toolCallId: message.messageType.value.toolCallId,
        name: message.messageType.value.name,
        content: message.messageType.value.content,
      };
    case undefined:
      throw new Error(`Undefined message type`);
    default:
      message.messageType satisfies never;
      throw new Error(
        `Unknown message type: ${(message as ConversationMessage).messageType.case}`,
      );
  }
};
